import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Camera, X, Plus, Stack, ArrowSquareOut } from "@phosphor-icons/react";
import BOQItemPicker from "@/components/BOQItemPicker";

// Subcategory hints (for the flat-wise "fitting" categories — same hints as Material PO)
const FLAT_SUBCATS = {
  "Plumbing Pipes": ["Pipes", "Collar", "Elbow", "Tee", "Coupling", "Reducer"],
  "Bathroom Fittings": ["Taps", "Showers", "Basins", "Toilet Seats", "Pipes"],
  "Electrical Fittings": ["Wires", "Switches", "Sockets", "Boards", "Conduits"],
  "Wooden Doors": ["Flush Door", "Panel Door", "Solid Wood"],
  "Door Frames": ["Teak", "Sal", "Steel"],
  UPVC: ["Sliding Window", "Casement Window", "Ventilator"],
  Paint: ["Emulsion", "Enamel", "Primer", "Distemper"],
  Lights: ["LED Panel", "Downlight", "Tubelight", "Chandelier"],
  POP: ["Plaster", "Punning"],
  Cement: ["OPC 43", "OPC 53", "PPC"],
  "Fabrication Grill": ["Window Grill", "Gate", "Railing"],
  "Rock Cement": ["Standard"],
  Others: [],
};

const DEFAULT_UNITS = {
  Concrete: "cum", Tiles: "boxes", Steel: "kg", Bricks: "nos",
  Sand: "tonnes", Jelly: "tonnes",
  Cement: "bags", Paint: "litres", "Plumbing Pipes": "nos",
};

export default function OutwardCreate() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { activeProjectId } = useProject();

  const [type, setType] = useState("bulk"); // bulk | flat
  const [cats, setCats] = useState({ bulk: [], flat: [], sand_subtypes: [] });

  const [form, setForm] = useState({
    category: "",
    subcategory: "",
    flat_number: "",
    site_location: "",
    quantity: "",
    unit: "nos",
    issued_to: "",
    notes: "",
    boq_item_id: null,
  });
  const [photos, setPhotos] = useState([]); // [{file, previewUrl}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/catalog/outward-categories");
      setCats(data);
    })();
  }, []);

  const availableCategories = type === "bulk" ? cats.bulk : cats.flat;
  const subcatOptions =
    type === "bulk" && form.category === "Sand"
      ? cats.sand_subtypes
      : type === "flat" && FLAT_SUBCATS[form.category]
      ? FLAT_SUBCATS[form.category]
      : [];

  const handleCategoryChange = (v) => {
    setForm({ ...form, category: v, subcategory: "", unit: DEFAULT_UNITS[v] || form.unit });
  };

  const onPhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/"));
    const newPhotos = valid.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      URL.revokeObjectURL(prev[idx].previewUrl);
      return next;
    });
  };

  const canSave =
    form.category &&
    form.boq_item_id &&
    parseFloat(form.quantity) > 0 &&
    form.unit &&
    (type === "bulk" || form.flat_number.trim());

  const handleSubmit = async () => {
    if (!canSave) {
      if (!form.boq_item_id) { toast.error("Please pick the BOQ item this outward is against."); return; }
      toast.error(type === "flat" ? "Flat number, category and quantity are required." : "Category and quantity are required.");
      return;
    }
    if (!activeProjectId) {
      toast.error("Select a project from the sidebar first.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("category", form.category);
      fd.append("subcategory", form.subcategory || "");
      fd.append("flat_number", form.flat_number || "");
      fd.append("site_location", form.site_location || "");
      fd.append("quantity", String(parseFloat(form.quantity)));
      fd.append("unit", form.unit || "nos");
      fd.append("issued_to", form.issued_to || "");
      fd.append("notes", form.notes || "");
      fd.append("project_id", activeProjectId);
      fd.append("boq_item_id", form.boq_item_id);
      photos.forEach((p) => fd.append("photos", p.file, p.file.name));

      const { data } = await api.post("/outward", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Outward entry recorded");
      navigate(`/outward/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 sm:p-10 max-w-[1200px] mx-auto" data-testid="outward-create-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">New Issue</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Issue Material Outward</h1>
        <p className="text-sm text-neutral-500 mt-2">
          Record material going out from store — bulk site outflow or flat-wise consumption with photo trail.
        </p>
      </div>

      {/* Type toggle */}
      <Tabs value={type} onValueChange={(v) => { setType(v); setForm({ ...form, category: "", subcategory: "" }); }} className="mb-6">
        <TabsList className="grid w-full max-w-md grid-cols-2 rounded-sm">
          <TabsTrigger value="bulk" className="rounded-sm gap-2" data-testid="type-tab-bulk">
            <Stack size={14} /> Bulk Outward
          </TabsTrigger>
          <TabsTrigger value="flat" className="rounded-sm gap-2" data-testid="type-tab-flat">
            <ArrowSquareOut size={14} /> Flat-wise
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Category + qty */}
          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">
              {type === "bulk" ? "Bulk Material (Site-level)" : "Flat-wise Material"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Category *</Label>
                <Select value={form.category} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="outward-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">
                  {type === "bulk" && form.category === "Sand" ? "Sand Type" : "Subcategory"}
                </Label>
                {subcatOptions.length > 0 ? (
                  <Select value={form.subcategory} onValueChange={(v) => setForm({ ...form, subcategory: v })}>
                    <SelectTrigger className="rounded-sm h-11" data-testid="outward-subcategory-select">
                      <SelectValue placeholder="Select (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {subcatOptions.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.subcategory}
                    onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                    placeholder={form.category ? "Optional (free text)" : "Select category first"}
                    className="rounded-sm h-11"
                    disabled={!form.category}
                  />
                )}
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <BOQItemPicker
                  projectId={activeProjectId}
                  value={form.boq_item_id}
                  onChange={(id) => setForm({ ...form, boq_item_id: id })}
                  required
                  label="BOQ Item (which line item is this outward against?)"
                  testId="outward-boq-picker"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Quantity *</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="rounded-sm h-11 text-right tabular-nums"
                  data-testid="outward-qty-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Unit</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="nos / kg / bags / cum…"
                  className="rounded-sm h-11"
                  data-testid="outward-unit-input"
                />
              </div>

              {type === "flat" ? (
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Flat Number *</Label>
                  <Input
                    value={form.flat_number}
                    onChange={(e) => setForm({ ...form, flat_number: e.target.value })}
                    placeholder="e.g. A-101, Tower-B/302"
                    className="rounded-sm h-11 font-mono-num"
                    data-testid="outward-flat-input"
                  />
                </div>
              ) : (
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Flat Number (optional)</Label>
                  <Input
                    value={form.flat_number}
                    onChange={(e) => setForm({ ...form, flat_number: e.target.value })}
                    placeholder="Tag a flat if applicable (optional for bulk)"
                    className="rounded-sm h-11 font-mono-num"
                    data-testid="outward-flat-input"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Site Location</Label>
                <Input
                  value={form.site_location}
                  onChange={(e) => setForm({ ...form, site_location: e.target.value })}
                  placeholder="Block / Tower / Wing"
                  className="rounded-sm h-11"
                  data-testid="outward-site-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Issued To / Received By</Label>
                <Input
                  value={form.issued_to}
                  onChange={(e) => setForm({ ...form, issued_to: e.target.value })}
                  placeholder="Mason / contractor / worker name"
                  className="rounded-sm h-11"
                  data-testid="outward-issued-to-input"
                />
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-sm min-h-[80px]"
                  placeholder="Any condition, batch, dispatch reference..."
                  data-testid="outward-notes-input"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Photo Trail</div>
                <div className="text-sm text-neutral-700 mt-0.5">
                  {type === "flat" ? "Capture photos of material delivered to the flat" : "Optional photos for the bulk delivery"}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="rounded-sm h-9 gap-1.5"
                data-testid="add-photos-btn"
              >
                <Camera size={14} /> Add Photos
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={onPhotoSelect}
                className="hidden"
                data-testid="photo-file-input"
              />
            </div>
            {photos.length === 0 ? (
              <div className="border border-dashed border-neutral-300 rounded-sm py-10 text-center">
                <Camera size={28} className="mx-auto text-neutral-400 mb-2" />
                <div className="text-sm text-neutral-500">No photos added</div>
                <div className="text-xs text-neutral-400 mt-1">Tap "Add Photos" — you can pick multiple or use camera</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative group" data-testid={`photo-preview-${i}`}>
                    <img src={p.previewUrl} alt={`Photo ${i + 1}`} className="w-full aspect-square object-cover rounded-sm border border-neutral-200" />
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 h-6 w-6 bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X size={12} />
                    </button>
                    <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 px-1 truncate">{p.file.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-sm p-6 lg:sticky lg:top-8">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Summary</div>
            <div className="space-y-2.5 text-sm mb-6">
              <Row label="Type" value={type === "bulk" ? "Bulk Outward" : "Flat-wise"} />
              <Row label="Category" value={form.category || "—"} />
              {form.subcategory && <Row label="Subcategory" value={form.subcategory} />}
              <Row label="Quantity" value={form.quantity ? `${form.quantity} ${form.unit}` : "—"} />
              {form.flat_number && <Row label="Flat" value={form.flat_number} mono />}
              {form.site_location && <Row label="Site" value={form.site_location} />}
              <Row label="Photos" value={photos.length > 0 ? `${photos.length} attached` : "None"} />
            </div>
            <Button
              className="w-full rounded-sm h-11 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2"
              onClick={handleSubmit}
              disabled={!canSave || saving}
              data-testid="save-outward-btn"
            >
              <Plus size={16} weight="bold" />
              {saving ? "Recording…" : "Record Outward Entry"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-neutral-500 text-xs uppercase tracking-wider">{label}</span>
      <span className={mono ? "font-mono-num text-right" : "text-right"}>{value}</span>
    </div>
  );
}
